import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MembershipCard } from '../components/MembershipCard';
import { Button } from '../components/ui/button';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AppShell from '../components/AppShell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Memberships() {
  const [tiers, setTiers] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const { user, loading: authLoading, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchTiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const fetchTiers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/memberships/tiers`);
      setTiers(response.data);
    } catch (error) {
      toast.error('Failed to load membership tiers');
    }
  };

  const handleSelectTier = async (tierId) => {
    if (user.membership_tier === tierId) {
      return;
    }

    // Check if Founder tier is available
    if (tierId === 'founder' && tiers.founder) {
      if (!tiers.founder.is_open) {
        toast.error('Founder membership is currently closed');
        return;
      }
      if (tiers.founder.seats_remaining <= 0) {
        toast.error('All Founder seats have been filled');
        return;
      }
    }

    setLoading(true);
    setSelectedTier(tierId);
    try {
      const originUrl = window.location.origin;
      // Built-in tiers (founder/growth/ascend) → recurring subscription flow.
      const isBuiltIn = ['founder', 'growth', 'ascend'].includes(tierId);
      const endpoint = isBuiltIn
        ? `${API_URL}/api/subscriptions/checkout`
        : `${API_URL}/api/memberships/upgrade`;
      const response = await axios.post(
        endpoint,
        { tier: tierId, origin_url: originUrl },
        { headers: getAuthHeader() }
      );

      const redirect = response.data.checkout_url || response.data.url;
      window.location.href = redirect;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process upgrade');
      setLoading(false);
      setSelectedTier(null);
    }
  };

  if (!user) return null;

  const tierOrder = ['ascend', 'growth', 'founder'];

  return (
    <AppShell active="memberships" title="Memberships">
      <div className="py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-playfair font-bold text-white mb-4">Choose Your Path</h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Select the membership tier that aligns with your business goals
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8" data-testid="membership-cards-grid">
          {tierOrder.map((key) => {
            const tier = tiers[key];
            if (!tier) return null;
            
            return (
              <MembershipCard
                key={key}
                tier={key}
                name={tier.name}
                price={tier.price}
                features={tier.features}
                onSelect={() => handleSelectTier(key)}
                isCurrentTier={user.membership_tier === key}
                disabled={loading && selectedTier === key}
                initiationFee={tier.initiation_fee || 0}
                seatsRemaining={key === 'founder' ? tier.seats_remaining : null}
                isOpen={key === 'founder' ? tier.is_open : true}
              />
            );
          })}
        </div>

        <div className="mt-16 max-w-3xl mx-auto wm-card p-8 rounded-lg">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-xl font-playfair font-bold mb-4 text-foreground">Important Information</h3>
              <div className="text-sm text-muted-foreground space-y-2">
                <p>• Membership fees provide access to education, community, and platform tools.</p>
                <p>• Only Founder members are eligible for the $1 Million leaderboard and award.</p>
                <p>• Founder membership includes a one-time $500 initiation fee charged with first payment.</p>
                <p>• Recognition and rewards are based solely on verified sales performance.</p>
                <p>• We do not guarantee income or financial results.</p>
                <p>• This is not an investment vehicle or passive income program.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </AppShell>
  );
}
